import PropTypes from 'prop-types';
import React from 'react';
import { LocaleService } from '@sm360/widget-commons-core-services';
import { ProfileService, AccountService } from '@sm360/widget-commons-general';

import GroupResponseService from 'commons-forms/services/GroupResponseService/GroupResponseService.js';
import ValidationService from 'commons-forms/services/ValidationService/ValidationService.js';

import InputPhoneNumber from 'commons-forms/components/InputPhoneNumber/InputPhoneNumber.jsx';
import InputText from 'commons-forms/components/InputText/InputText.jsx';
import InputEmail from 'commons-forms/components/InputEmail/InputEmail.jsx';

import FIELD_LENGTH from 'commons-forms/constants/fieldLength.js';

class IdentityGroup extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            formValidation: {
                firstname: ValidationService.validateIsNotEmpty(props.values.firstname),
                lastname: ValidationService.validateIsNotEmpty(props.values.lastname),
                phoneNumber: ValidationService.validatePhoneNumber(props.values.phoneNumber),
                email: ValidationService.validateEmail(props.values.email),
            },
            profile: {},
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handlePhoneNumberChange = this.handlePhoneNumberChange.bind(this);
        this.handleFormSubmission = this.handleFormSubmission.bind(this);
        this.sendResponse = this.sendResponse.bind(this);
    }

    componentDidMount() {
        // We need make an api call here so as to get all the information needed to prepopulate our form.
        // We COULD solely rely on props theoretically, but until we have a single redux store,
        // we are better off double checking here...
        // TODO: Remove this Api call.
        if (AccountService.isAuthenticated()) {
            ProfileService.getLoggedAccountProfile().then((profile) => {
                this.setState({ profile }, this.forceFormValidation);
            });
        }
    }

    /**
     * Using this method to have number formatted to same way so we can compare them
     * (typically because we are preffixing number in state with +1 and some user don't have number preffixed yet)
     */
    normalizePhoneNumber(phoneNumber) {
        if (!phoneNumber || !ValidationService.validatePhoneNumber(phoneNumber)) {
            return '';
        }
        return String(phoneNumber)
            .replace('+1', '')
            .trim();
    }

    forceFormValidation() {
        this.setState(
            (oldState) => ({
                formValidation: {
                    firstname: ValidationService.validateIsNotEmpty(oldState.profile.firstname),
                    lastname: ValidationService.validateIsNotEmpty(oldState.profile.lastname),
                    phoneNumber: ValidationService.validatePhoneNumber(oldState.profile.homePhone),
                    email: ValidationService.validateEmail(oldState.profile.email1),
                },
            }),
            () => {
                const groupResponse = this.createGroupResponse();
                this.props.onChange(groupResponse);
            }
        );
    }

    handlePhoneNumberChange(phoneNumberResponse) {
        this.setState({
            formValidation: {
                ...this.state.formValidation,
                phoneNumber: phoneNumberResponse.validation,
            },
            profile: {
                ...this.state.profile,
                [phoneNumberResponse.name]: phoneNumberResponse.value.startsWith('+1')
                    ? `+1${phoneNumberResponse.value.slice(-10)}`
                    : `+1${phoneNumberResponse.value}`,
            },
        });
    }

    handleInputChange(value, name) {
        if (name === 'email') {
            this.setState({
                formValidation: {
                    ...this.state.formValidation,
                    email: ValidationService.validateEmail(value),
                },
                profile: {
                    ...this.state.profile,
                    email1: value,
                },
            });
        } else {
            this.setState({
                formValidation: {
                    ...this.state.formValidation,
                    [name]: ValidationService.validateIsNotEmpty(value),
                },
                profile: {
                    ...this.state.profile,
                    [name]: value,
                },
            });
        }
    }

    handleFormSubmission() {
        if (AccountService.isAuthenticated() && ValidationService.validateForm(this.state.formValidation)) {
            ProfileService.updateAccountProfile(this.state.profile);
        }
    }

    sendResponse() {
        const groupResponse = this.createGroupResponse();
        // TODO: Eventually only send this form when the value entered is different from the one entered.
        this.handleFormSubmission();
        this.props.onChange(groupResponse);
    }

    createGroupResponse() {
        const areFieldsValid = ValidationService.validateForm(this.state.formValidation);

        // TODO: Eventually refactor this so we can bubble up the two phone numbers, home and mobile.
        const inputs = {
            email: this.state.profile.email1,
            firstname: this.state.profile.firstname,
            lastname: this.state.profile.lastname,
            phoneNumber: this.state.profile.homePhone,
        };

        const groupResponse = GroupResponseService.createResponse(
            areFieldsValid,
            inputs,
            'error.group.identity',
            null,
            'identityGroup'
        );

        return groupResponse;
    }

    render() {
        return (
            <div>
                <InputText
                    type="text"
                    name="firstname"
                    classNames={['styled-placeholder half']}
                    label={this.props.labels.firstname}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.FIRST_NAME')}
                    value={this.state.profile.firstname}
                    isValid={this.state.formValidation.firstname}
                    isRequired
                    valueChange={this.handleInputChange}
                    onBlur={this.sendResponse}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    dataTest={this.props.dataTestPrefix ? `${this.props.dataTestPrefix}.firstname` : 'firstname'}
                    maxLength={FIELD_LENGTH.FIRST_NAME_MAX}
                />

                <InputText
                    type="text"
                    name="lastname"
                    classNames={['styled-placeholder half']}
                    label={this.props.labels.lastname}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.LAST_NAME')}
                    value={this.state.profile.lastname}
                    isValid={this.state.formValidation.lastname}
                    isRequired
                    valueChange={this.handleInputChange}
                    onBlur={this.sendResponse}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    dataTest={this.props.dataTestPrefix ? `${this.props.dataTestPrefix}.lastname` : 'lastname'}
                    maxLength={FIELD_LENGTH.LAST_NAME_MAX}
                />

                <InputPhoneNumber
                    type="tel"
                    name="homePhone"
                    classNames={['styled-placeholder half']}
                    label={this.props.labels.phoneNumber}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.PHONE_NUMBER')}
                    value={this.state.profile.homePhone}
                    isValid={this.state.formValidation.phoneNumber}
                    isRequired
                    valueChange={this.handlePhoneNumberChange}
                    onBlur={this.sendResponse}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    dataTest={this.props.dataTestPrefix ? `${this.props.dataTestPrefix}.phone` : 'phone'}
                />
                <InputEmail
                    label={this.props.labels.email}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.EMAIL')}
                    classNames={['styled-placeholder half']}
                    value={this.state.profile.email1}
                    isValid={this.state.formValidation.email}
                    isRequired
                    valueChange={this.handleInputChange}
                    onBlur={this.sendResponse}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    dataTest={this.props.dataTestPrefix ? `${this.props.dataTestPrefix}.email` : 'email'}
                />
            </div>
        );
    }
}

IdentityGroup.defaultProps = {
    values: {
        firstname: '',
        lastname: '',
        email: '',
        phoneNumber: '',
    },
    hasBeenSubmit: false,
    dataTestPrefix: '',
};

IdentityGroup.propTypes = {
    labels: PropTypes.shape({
        email: PropTypes.string.isRequired,
        firstname: PropTypes.string.isRequired,
        lastname: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string.isRequired,
    }).isRequired,
    onChange: React.PropTypes.func.isRequired,
    hasBeenSubmit: PropTypes.bool,
    values: PropTypes.shape({
        email: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        phoneNumber: PropTypes.string,
    }),
    dataTestPrefix: PropTypes.string,
};

IdentityGroup.displayName = 'IdentityGroup';

export default IdentityGroup;



// WEBPACK FOOTER //
// ./src/components/Groups/IdentityGroup.jsx